import { ChangeEvent } from 'react';

import { Checkbox, Controller, RadioButtonGroup } from '@selina-finance/ui';

import {
    externalCladdingHardStopMessage,
    impactToPropertyValueOptions,
    isHelpToBuyNotRepaid,
} from '@src/app/formModels/eligibility/yourProperty';
import { radioButtonOptions } from '@src/shared/helpers/input';
import FormTextarea from '@src/ui/components/FormTextarea/FormTextarea';

interface IHelpToBuyFields {
    formikProps: any;
    getError: (fieldName: string) => any;
    claddingError: string;
    onCladdingError: (error: string) => void;
}

const HelpToBuyFields = ({
    formikProps,
    getError,
    claddingError,
    onCladdingError: setCladdingError,
}: IHelpToBuyFields) => {
    if (
        !isHelpToBuyNotRepaid(
            formikProps.values.ownershipSchemeType,
            formikProps.values.ownershipSchemeRepaid,
        )
    ) {
        return null;
    }

    const renderTextarea = (name: string) => (
        <FormTextarea
            name={name}
            value={formikProps.values[name] || ''}
            onChange={formikProps.handleChange}
            onBlur={formikProps.handleBlur}
            error={getError(name)}
            maxLength={500}
        />
    );

    return (
        <>
            <Controller label='Is there anything that may affect the value of your property?'>
                <RadioButtonGroup
                    name='hasPropertyValueAffectingFactors'
                    id='hasPropertyValueAffectingFactors'
                    value={
                        formikProps.values.hasPropertyValueAffectingFactors?.toString() ||
                        ''
                    }
                    error={{
                        hasError: false,
                        errorMessage: getError(
                            'hasPropertyValueAffectingFactors',
                        ),
                    }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        formikProps.setTouched({
                            ...formikProps.touched,
                            hasPropertyValueAffectingFactors: true,
                        });
                        if (e.currentTarget.value !== 'true') {
                            setCladdingError('');
                            formikProps.setFieldTouched(
                                'impactToPropertyValue',
                                false,
                            );
                            formikProps.setFieldValue(
                                'impactToPropertyValue',
                                impactToPropertyValueOptions,
                            );
                        } else if (
                            !formikProps.values.impactToPropertyValue?.length
                        ) {
                            formikProps.setFieldValue(
                                'impactToPropertyValue',
                                impactToPropertyValueOptions,
                            );
                        }
                        formikProps.handleChange(e);
                    }}
                    options={radioButtonOptions}
                />
            </Controller>

            {formikProps.values.hasPropertyValueAffectingFactors === 'true' && (
                <Controller label='Which of the following may affect the value of your property?'>
                    <div className='grid grid-cols-1 gap-4'>
                        {formikProps.values.impactToPropertyValue?.map(
                            (option: any, index: number) => (
                                <div key={option.value}>
                                    <Checkbox
                                        name={`impactToPropertyValue_${index}`}
                                        value={option.value}
                                        checked={!!option.checked}
                                        error={
                                            option.value ===
                                                'External cladding' &&
                                            !!claddingError
                                                ? { hasError: true }
                                                : undefined
                                        }
                                        onChange={(
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                            const updated =
                                                formikProps.values.impactToPropertyValue.map(
                                                    (opt: any) =>
                                                        opt.value ===
                                                        option.value
                                                            ? {
                                                                  ...opt,
                                                                  checked:
                                                                      e.target
                                                                          .checked,
                                                              }
                                                            : opt,
                                                );
                                            if (
                                                option.value ===
                                                'External cladding'
                                            ) {
                                                setCladdingError(
                                                    e.target.checked
                                                        ? externalCladdingHardStopMessage
                                                        : '',
                                                );
                                            }
                                            formikProps.setFieldTouched(
                                                'impactToPropertyValue',
                                                true,
                                            );
                                            formikProps.setFieldValue(
                                                'impactToPropertyValue',
                                                updated,
                                            );
                                        }}
                                    >
                                        {option.label}
                                    </Checkbox>
                                    {option.value === 'External cladding' &&
                                        claddingError && (
                                            <p className='text-red-500 text-sm mt-2'>
                                                {claddingError}
                                            </p>
                                        )}
                                </div>
                            ),
                        )}
                        {getError('impactToPropertyValue') &&
                            !claddingError && (
                                <p className='text-red-500 text-sm'>
                                    {getError('impactToPropertyValue')}
                                </p>
                            )}
                    </div>
                </Controller>
            )}

            {formikProps.values.impactToPropertyValue?.some(
                (opt: any) => opt.value === 'Other' && opt.checked,
            ) && (
                <Controller
                    label='Describe any other issues in as much detail as possible'
                    additionalInfo='Maximum 500 characters'
                >
                    {renderTextarea('impactToPropertyValueOther')}
                </Controller>
            )}

            <Controller label='Have you made structural alterations to your property?'>
                <RadioButtonGroup
                    name='hasStructuralAlterationsToProperty'
                    options={radioButtonOptions}
                    error={{
                        hasError: false,
                        errorMessage: getError(
                            'hasStructuralAlterationsToProperty',
                        ),
                    }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        formikProps.handleChange(e);
                        if (e.currentTarget.value !== 'true') {
                            formikProps.setFieldValue(
                                'structuralAlterationsApprovedByHomesEngland',
                                null,
                            );
                            formikProps.setFieldValue(
                                'structuralAlterationsDetails',
                                '',
                            );
                        }
                    }}
                    value={
                        formikProps.values.hasStructuralAlterationsToProperty
                    }
                />
            </Controller>

            {formikProps.values.hasStructuralAlterationsToProperty ===
                'true' && (
                <>
                    <Controller label='Are the structural alterations approved by Homes England?'>
                        <RadioButtonGroup
                            name='structuralAlterationsApprovedByHomesEngland'
                            options={radioButtonOptions}
                            error={{
                                hasError: false,
                                errorMessage: getError(
                                    'structuralAlterationsApprovedByHomesEngland',
                                ),
                            }}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            value={
                                formikProps.values
                                    .structuralAlterationsApprovedByHomesEngland
                            }
                        />
                    </Controller>
                    <Controller
                        label='Describe the structural alterations in as much detail as possible'
                        additionalInfo='Maximum 500 characters'
                    >
                        {renderTextarea('structuralAlterationsDetails')}
                    </Controller>
                </>
            )}
        </>
    );
};

export default HelpToBuyFields;
